/**
 * Unified entry point for the "Get My Matches" CTA — R103.
 *
 * State-aware routing so users are not forced through the 8-question
 * assessment again once they already have a Business Profile:
 *
 *   Logged-out                             → /login (?next=... if provided)
 *   No Business Profile                    → /onboarding  (create the first one)
 *   Draft profile (assessment incomplete)  → /onboarding?profile_id=<id>  (resume)
 *   One completed profile                  → /recommendations  (activates it first)
 *   Multiple completed profiles, one active → /matches
 *   Multiple completed, none active         → /business-profiles  (pick one first)
 *   Admin                                   → /admin
 *
 * The optional `next` param is honored ONLY for the logged-out branch (post-auth
 * deep-link); logged-in users always follow their state-aware route because that
 * matches product intent even when they entered the flow from a specific page.
 */
import api from "@/lib/api";

const DEFAULT_NEXT = "/onboarding";

/**
 * Prefetch likely-next route chunks. Fire on hover / focus of the primary
 * "Get My Matches" CTA so the following click feels instant.
 *
 * We only warm the two most-visited follow-on routes (Pricing + BusinessIdeas)
 * plus the flow's own next hop (Onboarding). Each dynamic import is idempotent
 * — the browser dedupes repeat fetches — so calling this on every hover is
 * cheap. Guarded so it runs at most once per session per route.
 */
const _prefetched = new Set();
export function prefetchNextRoutes() {
  if (typeof window === "undefined") return;
  const warm = (key, loader) => {
    if (_prefetched.has(key)) return;
    _prefetched.add(key);
    // Fire-and-forget; ignore failures (chunk will just load on click).
    loader().catch(() => _prefetched.delete(key));
  };
  warm("pricing",        () => import("@/pages/Plans"));
  warm("business-ideas", () => import("@/pages/BusinessIdeas"));
  warm("onboarding",     () => import("@/pages/Onboarding"));
  warm("login",          () => import("@/pages/Login"));
}

/** Programmatic navigation for the "Get My Matches" CTA.
 *
 * R110.40 — Onboarding-first flow: logged-out users go DIRECTLY to the
 * 8-question assessment. Their answers are stashed in sessionStorage while
 * the auth step runs, then flushed to the API immediately after signup so
 * they land on `/recommendations` (or the `next` deep-link) with matches ready.
 *
 * R110.41 — SEO/blog CTAs pass a `next` deep-link (e.g. `/venture/x/feasibility`)
 * so that after the 8Q → signup handoff the user is returned to the original
 * page they clicked from — not the generic `/recommendations`.
 */
export async function startRecommendationFlow({ user, nav, next = DEFAULT_NEXT, prefill = null }) {
  // Logged-out → onboarding (the answers cache handles the post-auth handoff).
  if (!user) {
    const qs = new URLSearchParams();
    // Deep-link `next` is honored via the guest questionnaire cache flush.
    if (next && next !== DEFAULT_NEXT) qs.set("next", next);
    Object.entries(prefill || {}).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const q = qs.toString();
    nav(q ? `/onboarding?${q}` : "/onboarding");
    return;
  }
  if (user.role === "admin") {
    nav("/admin");
    return;
  }
  // Logged-in customer — apply state-aware routing.
  try {
    const { data } = await api.get("/business-profiles");
    const profiles = data?.profiles || [];

    if (profiles.length === 0) {
      nav("/onboarding");
      return;
    }

    // Prefer the currently-active COMPLETED profile.
    const active = profiles.find(
      (p) => p.is_active && (p.status || "").toLowerCase() !== "draft"
    );
    if (active) {
      nav("/recommendations");
      return;
    }

    // Any draft (assessment incomplete) → resume it.
    const draft = profiles.find((p) => (p.status || "").toLowerCase() === "draft");
    if (draft) {
      nav(`/onboarding?profile_id=${draft.id}`);
      return;
    }

    // Multiple completed but none active → let the user pick.
    if (profiles.length > 1) {
      nav("/business-profiles");
      return;
    }

    // Single completed profile without is_active flag → activate then go.
    try { await api.post(`/business-profiles/${profiles[0].id}/activate`); } catch { /* noop */ }
    nav("/recommendations");
  } catch {
    // Fallback: safest default is onboarding.
    nav("/onboarding");
  }
}
